/**
 * ViewHero — shared page hero themed by the view's menu colour + icon.
 * Concept (mirrored from SchoolRideOps): the hero base matches the OVERALL menu
 * colour (the dark navy sidebar, `--sidebar`); the view's per-item icon colour is
 * used only as clear ACCENTS — the icon pill, a corner glow, the top accent line
 * and the large faint "ghost" icon. `icon` + `color` come from the sidebar entry.
 */
import { motion } from 'framer-motion';
import type { ReactNode, ElementType } from 'react';

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const rgba = (hex: string, a: number) => { const [r, g, b] = hexToRgb(hex); return `rgba(${r},${g},${b},${a})`; };

export function ViewHero({
  icon: Icon, color, title, subtitle, actions, children, compact,
}: {
  icon: ElementType;
  color: string;          // accent hex from the sidebar entry, e.g. '#3b82f6'
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;   // KPI row / extra content
  /** Niższy hero (Pulpit): mniejsze odstępy i ikona, tytuł i akcje w jednej linii. */
  compact?: boolean;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
      className={`relative rounded-2xl overflow-hidden px-6 text-white shrink-0 ${compact ? 'py-3.5' : 'py-5'}`}
      style={{
        background: 'linear-gradient(135deg, hsl(var(--sidebar)) 0%, #16203a 65%, #1e293b 100%)',
        boxShadow: '0 8px 28px rgba(2,6,23,0.35)',
      }}
    >
      {/* icon-colour glow accent bleeding from the top-right corner */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(120% 95% at 93% 4%, ${rgba(color, 0.50)}, transparent 55%)` }} />
      {/* top accent line in the view colour */}
      <div className="absolute top-0 inset-x-0 h-1" style={{ background: color }} />
      {/* large faint ghost icon, tinted with the view colour */}
      <Icon className="pointer-events-none absolute -right-6 -bottom-8 h-44 w-44 opacity-[0.16]" style={{ color }} strokeWidth={1.25} />

      <div className={`relative flex flex-wrap justify-between gap-x-4 ${compact ? 'items-center gap-y-2' : 'items-start gap-y-4'}`}>
        <div className={`flex items-center min-w-0 ${compact ? 'gap-3' : 'gap-4'}`}>
          {/* icon pill filled with the view colour — the headline accent */}
          <div className={`rounded-2xl flex items-center justify-center shrink-0 ring-1 ring-white/15 ${compact ? 'h-10 w-10' : 'h-12 w-12'}`}
            style={{ background: color, boxShadow: `0 4px 18px ${rgba(color, 0.55)}` }}>
            <Icon className={compact ? 'h-5 w-5 text-white' : 'h-6 w-6 text-white'} />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-extrabold leading-tight truncate">{title}</h1>
            {subtitle && <p className="text-white/55 text-[12px] mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0 flex-wrap">{actions}</div>}
      </div>

      {children && <div className={`relative ${compact ? 'mt-3' : 'mt-4'}`}>{children}</div>}
    </motion.div>
  );
}
